import { zodResolver } from '@hookform/resolvers/zod';
import { type CheckEmailOutputProvidersEnumKey, useAuthControllerCheckEmail } from 'korint-sdk';
import { IdentityProviders } from 'korint-sdk/react';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import { z } from 'zod';

import { Alert } from '../../ui/alert';
import { Button } from '../../ui/button';
import { Form } from '../../forms/form';
import { FormTextInput } from '../../forms/form-text-input';
import {
  getLastLoginMethod,
  getLastLoginMethodHint,
  saveLastLoginMethod,
} from '../../../utils/last-login-method';
import { IdentityProvider } from './identity-provider';
import { LightbulbIcon } from 'lucide-react';
import { Text } from '@folio/components/ui/text';

export type CheckEmailResult = {
  status: 'CONFIRMED' | 'PASSWORD_SETUP_REQUIRED';
  email: string;
};

const RETRY_ATTEMPTS = 3;
const RETRY_BASE_DELAY_MS = 500;

const retryWithBackoff = async <T,>(operation: () => Promise<T>): Promise<T> => {
  let lastError: unknown;
  for (let attempt = 0; attempt < RETRY_ATTEMPTS; attempt++) {
    try {
      return await operation();
    } catch (error) {
      lastError = error;
      if (attempt < RETRY_ATTEMPTS - 1) {
        await new Promise(resolve => setTimeout(resolve, RETRY_BASE_DELAY_MS * (attempt + 1)));
      }
    }
  }
  throw lastError;
};

export const EmailCheckForm = ({
  onResult,
  providers = [],
  showLastLoginHint = false,
  defaultEmail,
}: {
  onResult: (result: CheckEmailResult) => void;
  providers?: IdentityProviders[];
  showLastLoginHint?: boolean;
  defaultEmail?: string;
}) => {
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);
  const [ssoProviders, setSsoProviders] = useState<CheckEmailOutputProvidersEnumKey[] | null>(null);
  const { mutateAsync: checkEmail, isPending } = useAuthControllerCheckEmail();
  const [lastLoginMethodHint] = useState<string | null>(() =>
    showLastLoginHint ? getLastLoginMethodHint(getLastLoginMethod(), t) : null,
  );
  const [lastLoginMethod] = useState<string | null>(() =>
    showLastLoginHint ? getLastLoginMethod() : null,
  );

  const normalizedDefaultEmail = defaultEmail?.trim();
  const shouldLockEmail = z.string().email().safeParse(normalizedDefaultEmail).success;

  const resolverSchema = useMemo(
    () =>
      z.object({
        email: z.string().trim().email(t('authSignIn.validation.emailRequired')),
      }),
    [t],
  );

  const form = useForm({
    defaultValues: { email: normalizedDefaultEmail ?? '' },
    resolver: zodResolver(resolverSchema),
  });

  const watchedEmail = form.watch('email');
  useEffect(() => {
    setSsoProviders(null);
  }, [watchedEmail]);

  const handleSubmit = form.handleSubmit(async ({ email }) => {
    setError(null);
    setSsoProviders(null);
    try {
      const response = await retryWithBackoff(() => checkEmail({ data: { email } }));

      if (response.status === 'SSO_REQUIRED') {
        setSsoProviders(response.providers ?? []);
        return;
      }

      if (showLastLoginHint) {
        saveLastLoginMethod('email');
      }
      onResult({ status: response.status, email });
    } catch {
      setError(t('authSignIn.errors.unknown'));
    }
  });

  return (
    <Form {...form}>
      {shouldLockEmail ? (
        <Alert className="mb-4" variant="info">
          <LightbulbIcon />
          <Text inline size="sm">
            {t('authSignIn.emailLockedHint')}
          </Text>
        </Alert>
      ) : (
        lastLoginMethodHint && (
          <Alert className="mb-4" variant="info">
            <LightbulbIcon />
            <Text inline size="sm">
              {lastLoginMethodHint}
            </Text>
          </Alert>
        )
      )}

      <IdentityProvider
        providers={providers}
        context="signin"
        lastUsedProvider={lastLoginMethod}
        enableLastLoginTracking={showLastLoginHint}
      />

      <form onSubmit={handleSubmit} className="flex flex-col gap-3">
        {error && (
          <Alert className="mb-4" variant="destructive">
            {error}
          </Alert>
        )}
        <FormTextInput
          name="email"
          type="email"
          label={t('authSignIn.emailLabel')}
          placeholder={t('authSignIn.emailPlaceholder')}
          required
          disabled={shouldLockEmail}
          control={form.control}
        />
        {ssoProviders !== null && (
          <Alert className="mb-2" variant="destructive">
            {ssoProviders.length > 0
              ? formatSsoMessage(ssoProviders, t)
              : t('authSignIn.ssoMessageGeneric')}
          </Alert>
        )}
        <Button
          className="w-full"
          type="submit"
          loading={isPending}
          disabled={ssoProviders !== null}
        >
          {t('authSignIn.continueButton')}
        </Button>
        <ForgotPasswordLink email={form.watch('email')} />
      </form>
    </Form>
  );
};

const formatSsoMessage = (
  providers: CheckEmailOutputProvidersEnumKey[],
  t: (key: string, options?: Record<string, unknown>) => string,
): string => {
  if (providers.length === 1) {
    const [provider] = providers;
    return t('authSignIn.ssoMessageSingle', { provider });
  }
  const [first, ...rest] = providers;
  const last = rest[rest.length - 1];
  const head = [first, ...rest.slice(0, -1)].join(', ');
  return t('authSignIn.ssoMessageMultiple', { head, last });
};

const emailSchema = z.string().trim().email();

const ForgotPasswordLink = ({ email }: { email: string }) => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const isValidEmail = emailSchema.safeParse(email).success;

  const search = new URLSearchParams(searchParams);
  search.set('email', email);

  if (!isValidEmail) {
    return (
      <Text size="sm" tone="muted" className="text-center">
        {t('authSignIn.forgotPassword')}
      </Text>
    );
  }

  return (
    <Text size="sm" className="text-center">
      <Link
        to={{ pathname: '/auth/setup-password', search: search.toString() }}
        className="font-semibold text-primary hover:text-primary/90"
      >
        {t('authSignIn.forgotPassword')}
      </Link>
    </Text>
  );
};
